import type { SequenceData } from '@/core/annotations'
import type { ConsensusRasterRemap } from '@/modules/Editor/models/raster/consensusRasterRemap'
import { getVideoOrVoxelDataMapping } from '@/modules/Editor/plugins/mask/utils/shared/getVideoOrVoxelDataMapping'
import { decodeDenseRLE } from '@/modules/Editor/plugins/mask/rle/denseRle'
import type { MultiFrameRaster } from '@/modules/Editor/models/raster/rasterTypes'

/**
 * Rewrites every per-frame `mask_annotation_ids_mapping` so its keys are the
 * default-group annotation ids, dropping entries with no remap match. This
 * lets the rest of the deserializer work with default-group ids throughout.
 */
const remapVideoFramesMaskIds = (
  frames: SequenceData['frames'],
  remap: ConsensusRasterRemap,
): SequenceData['frames'] => {
  const remappedFrames: SequenceData['frames'] = {}

  Object.keys(frames).forEach((frameIndexString) => {
    const frameIndex = Number(frameIndexString)
    const frame = frames[frameIndex]
    const rasterLayer = frame.raster_layer
    if (rasterLayer === undefined) {
      remappedFrames[frameIndex] = frame
      return
    }

    const remappedMapping: Record<string, number> = {}
    Object.keys(rasterLayer.mask_annotation_ids_mapping).forEach((sourceId) => {
      const defaultId = remap.sourceIdToDefaultId.get(sourceId)
      if (!defaultId) {
        return
      }
      const labelIndex = rasterLayer.mask_annotation_ids_mapping[sourceId]
      if (!labelIndex) {
        return
      }
      remappedMapping[defaultId] = labelIndex
    })

    remappedFrames[frameIndex] = {
      ...frame,
      raster_layer: {
        ...rasterLayer,
        mask_annotation_ids_mapping: remappedMapping,
      },
    }
  })

  return remappedFrames
}

/**
 * Deserializes the raster annotation and creates a VideoRaster.
 * The `applyCorrectionForLabelIndexes` flag referrs to the correction of the label index mapping.
 * More on the topic can be found below where the flag is checked
 *
 * When `consensusRemap` is provided, the raster is a non-default annotation
 * group overlay: per-frame mask ids are rewritten to the default-group ids
 * (entries with no match are dropped).
 */
export const deserializeVideoOrVoxelRaster = (
  raster: MultiFrameRaster,
  videoRasterLayerDataFrames: SequenceData['frames'],
  annotationIdToClassIdMapping: Record<string, number>,
  applyCorrectionForLabelIndexes: boolean = true,
  consensusRemap?: ConsensusRasterRemap,
): void => {
  // This variable is used to get all of the masks used on the whole video raster.
  const frames = consensusRemap
    ? remapVideoFramesMaskIds(videoRasterLayerDataFrames, consensusRemap)
    : videoRasterLayerDataFrames
  const dataMapping = getVideoOrVoxelDataMapping(frames, applyCorrectionForLabelIndexes)

  Object.keys(frames).forEach((frameIndexString) => {
    const frameIndex = Number(frameIndexString)
    const frame = frames[frameIndex]
    const rasterLayer = frame.raster_layer
    const frameLabelCorrection = dataMapping.frameLabelIndexCorrection[frameIndex]

    if (rasterLayer === undefined) {
      throw new Error('No raster_layer property on raster layer annotation')
    }

    const denseRLE = rasterLayer.dense_rle
    const totalPixels = rasterLayer.total_pixels

    if (!denseRLE || denseRLE[0] === undefined) {
      return
    }

    const { mask, boundsPerLabelIndex } = decodeDenseRLE(denseRLE, totalPixels, raster.width)

    /**
     *  Some annotation id <=> label index association can be wrong from the BE.
     *  Every frame has label index starting from 1
     *  Example:
     *  {
     *    frame1: { annotation_id_1: 1, annotation_id_2: 2 },
     *    frame2: { annotation_id_2: 1 }
     *  }
     *  In this case, annotation_id_2 was associated with label index 2 in frame1 and 1 in frame2.
     *  This loop is to apply the correction to keep it consistent, and doesn't need to be
     *  applied when the deserialization is triggered by an undo/redo action, as the data is
     *  already normalised
     */
    if (applyCorrectionForLabelIndexes) {
      for (let pixelIndex = 0; pixelIndex < mask.length; pixelIndex++) {
        const maskLabelIndex = mask[pixelIndex]
        if (maskLabelIndex === 0) {
          continue
        }
        mask[pixelIndex] = frameLabelCorrection[maskLabelIndex]
      }
    }

    // Set bounds for rasters on frame.
    Object.keys(rasterLayer.mask_annotation_ids_mapping).forEach((annotationId) => {
      const frameLabelIndex = rasterLayer.mask_annotation_ids_mapping[annotationId]
      const labelIndex = dataMapping.getLabelIndex(annotationId)
      if (
        labelIndex === undefined ||
        frameLabelIndex === undefined ||
        !boundsPerLabelIndex[frameLabelIndex]
      ) {
        // the label was previously present, but not anymore, remove it from the raster frame data
        raster.deleteLabelOnKeyframes(labelIndex || frameLabelIndex || 0, [frameIndex])
        return
      }

      const bounds = boundsPerLabelIndex[frameLabelIndex]

      raster.setLabelOnKeyframe(labelIndex, frameIndex, bounds)
    })

    // it's important to set the buffer at the end as updating the labels could delete an entire
    // keyframe, loosing the buffer
    raster.setActiveBufferForFrame(mask, frameIndex)
  })

  const annotationIds = Object.keys(dataMapping.annotationIdToLabelIndex)

  annotationIds.forEach((annotationId) => {
    const labelIndex = dataMapping.getLabelIndex(annotationId)
    if (labelIndex === undefined) {
      return
    }
    const classId = annotationIdToClassIdMapping[annotationId]

    if (classId === undefined) {
      return
    }

    raster.setAnnotationMapping(labelIndex, annotationId, classId)
  })
}
